import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import ccOnboardingApi, {
  STATUS_CODES,
  currentUserCode,
  uploadFormAttachments,
  uploadDraftFormAttachments,
} from "../services/ccOnboardingApi";
import { buildStepPayload } from "../utils/ccOnboardingPayloadMapper";

// Every write action (submit/approve/reject/resubmit/update) returns the
// same envelope: `{ success, message, ... }` on both the happy path and
// the failure path (see Onboarding_API_Responses.md §1). Surface that
// `message` as a toast right where the call resolves, so every screen
// that dispatches these thunks gets consistent success/failure feedback
// without having to wire its own toast per action.
const notifySuccess = (message, fallback) => toast.success(message || fallback);
const notifyError = (message, fallback) => toast.error(message || fallback);

const DRAFT_KEY_STORAGE = "cc_onboarding_draft_key";
const ONBOARDING_TYPE_STORAGE = "cc_onboarding_type";

// Moved to ccOnboardingApi so the workflow screen and these thunks cannot drift
// apart on who the acting user is — they had two different lookups, preferring
// two different fields of the stored login object.

/**
 * A draft survives a page refresh: keep only the draftKey (never the
 * form contents — those still live on the server once a step is saved)
 * in localStorage, so resuming after an accidental reload is "GET the
 * draft back", not "start over".
 */
function persistDraftKey(draftKey, onboardingType) {
  if (draftKey) {
    localStorage.setItem(DRAFT_KEY_STORAGE, draftKey);
    localStorage.setItem(ONBOARDING_TYPE_STORAGE, onboardingType || "CC");
  }
}

export function readPersistedDraftKey() {
  return {
    draftKey: localStorage.getItem(DRAFT_KEY_STORAGE) || null,
    onboardingType: localStorage.getItem(ONBOARDING_TYPE_STORAGE) || "CC",
  };
}

function clearPersistedDraftKey() {
  localStorage.removeItem(DRAFT_KEY_STORAGE);
  localStorage.removeItem(ONBOARDING_TYPE_STORAGE);
}

// ---------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------

export const fetchWorkflowChain = createAsyncThunk(
  "ccOnboarding/fetchWorkflowChain",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.getWorkflowChain();
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Could not load the approval chain");
    }
  }
);

/**
 * Saves one wizard step. Called on "Continue" (autosave-before-advance),
 * not on every keystroke — the API upserts per (draftKey, stepNo), so
 * repeat saves of the same step are cheap and safe.
 */
export const saveWizardStep = createAsyncThunk(
  "ccOnboarding/saveWizardStep",
  async ({ stepNo, form, centerCode, complete = true }, { getState, rejectWithValue }) => {
    try {
      const { draftKey, onboardingType } = getState().ccOnboarding;
      const uCode = currentUserCode();

      // If draftKey exists, upload draft files for this step first
      if (draftKey && form) {
        await uploadDraftFormAttachments({
          draftKey,
          form,
          onboardingType: onboardingType || "CC",
          userCode: uCode,
          stepNo,
        });
      }

      const body = await buildStepPayload(stepNo, form);
      const { data } = await ccOnboardingApi.saveStep({
        stepNo,
        draftKey: draftKey || undefined,
        // Which facility this step belongs to, when editing an existing one.
        //
        // Without it the draft row is written with CENTER_CODE NULL, and since
        // beginEdit clears the draft key for a facility with no parked edit, the
        // step also mints a fresh key. The result is a draft nothing can find
        // again: not the detail screen, not pendingEditDraftKey, not approve().
        // An edit abandoned before the final Update was simply lost, silently.
        centerCode: centerCode || undefined,
        userCode: uCode,
        onboardingType,
        complete,
        body,
      });
      return { stepNo, data };
    } catch (err) {
      return rejectWithValue({
        stepNo,
        message: err.response?.data?.message || `Step ${stepNo} could not be saved`,
      });
    }
  }
);

export const fetchDraft = createAsyncThunk(
  "ccOnboarding/fetchDraft",
  async (draftKey, { rejectWithValue }) => {
    try {
      const [draftRes, attachmentsRes] = await Promise.allSettled([
        ccOnboardingApi.getDraft(draftKey),
        ccOnboardingApi.listDraftAttachments({ draftKey }),
      ]);
      const data = draftRes.status === "fulfilled" ? draftRes.value.data : null;
      if (!data) throw new Error("Could not resume draft");

      const draftAttachments =
        attachmentsRes.status === "fulfilled"
          ? attachmentsRes.value.data?.data || attachmentsRes.value.data || []
          : [];

      if (Array.isArray(draftAttachments) && draftAttachments.length) {
        if (Array.isArray(data)) {
          data.push({ stepNo: "attachments", stepPayload: draftAttachments, attachments: draftAttachments });
        } else if (typeof data === "object") {
          data.attachments = draftAttachments;
        }
      }
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Could not resume the saved draft");
    }
  }
);

export const fetchDraftProgress = createAsyncThunk(
  "ccOnboarding/fetchDraftProgress",
  async (draftKey, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.getDraftProgress(draftKey);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Could not load step progress");
    }
  }
);

export const submitOnboarding = createAsyncThunk(
  "ccOnboarding/submitOnboarding",
  async ({ draftKey, form } = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState().ccOnboarding;
      const effectiveDraftKey = draftKey || state.draftKey;
      const { data } = await ccOnboardingApi.submitDraft({
        draftKey: effectiveDraftKey,
        userCode: currentUserCode(),
      });
      const generatedCode = data?.code || data?.centerCode;
      if (generatedCode && form) {
        await uploadFormAttachments({
          centerCode: generatedCode,
          form,
          onboardingType: state.onboardingType || "CC",
        });
      }
      notifySuccess(data?.message, "Submitted successfully");
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Submit failed");
      return rejectWithValue(message || "Submit failed");
    }
  }
);

export const approveCentre = createAsyncThunk(
  "ccOnboarding/approveCentre",
  async ({ centerCode, remarks, userCode }, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.changeStatus({
        centerCode,
        statusCode: STATUS_CODES.APPROVED,
        userCode: userCode || currentUserCode(),
        remarks,
      });
      notifySuccess(data?.message, "Approved successfully");
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Approve failed");
      return rejectWithValue(message || "Approve failed");
    }
  }
);

export const rejectCentre = createAsyncThunk(
  "ccOnboarding/rejectCentre",
  async ({ centerCode, remarks, userCode }, { rejectWithValue }) => {
    if (!remarks) {
      notifyError(null, "Remarks are required to reject a centre");
      return rejectWithValue("Remarks are required to reject a centre");
    }
    try {
      const { data } = await ccOnboardingApi.changeStatus({
        centerCode,
        statusCode: STATUS_CODES.REJECTED,
        userCode: userCode || currentUserCode(),
        remarks,
      });
      notifySuccess(data?.message, "Rejected successfully");
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Reject failed");
      return rejectWithValue(message || "Reject failed");
    }
  }
);

export const resubmitCentre = createAsyncThunk(
  "ccOnboarding/resubmitCentre",
  async ({ centerCode, draftKey, userCode, remarks }, { getState, rejectWithValue }) => {
    try {
      const effectiveDraftKey = draftKey || getState().ccOnboarding.draftKey;
      const { data } = await ccOnboardingApi.resubmit({
        centerCode,
        draftKey: effectiveDraftKey,
        userCode: userCode || currentUserCode(),
        remarks,
      });
      notifySuccess(data?.message, "Resubmitted successfully");
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Resubmit failed");
      return rejectWithValue(message || "Resubmit failed");
    }
  }
);

export const updateOnboarding = createAsyncThunk(
  "ccOnboarding/updateOnboarding",
  async ({ centerCode, form }, { getState, rejectWithValue }) => {
    try {
      const state = getState().ccOnboarding;
      const { draftKey } = state;
      const { data } = await ccOnboardingApi.update({
        centerCode,
        draftKey,
        userCode: currentUserCode(),
      });
      if (centerCode && form) {
        await uploadFormAttachments({
          centerCode,
          form,
          onboardingType: state.onboardingType || "CC",
        });
      }
      notifySuccess(data?.message, "Updated successfully");
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Update failed");
      return rejectWithValue(message || "Update failed");
    }
  }
);

export const fetchWorkQueue = createAsyncThunk(
  "ccOnboarding/fetchWorkQueue",
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.getWorkQueue(params);
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Could not load the work queue");
      return rejectWithValue(message || "Could not load the work queue");
    }
  }
);

export const fetchList = createAsyncThunk(
  "ccOnboarding/fetchList",
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.getList(params);
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Could not load the list");
      return rejectWithValue(message || "Could not load the list");
    }
  }
);

export const fetchDetail = createAsyncThunk(
  "ccOnboarding/fetchDetail",
  async (centerCode, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.getDetail(centerCode);
      return data;
    } catch (err) {
      const message = err.response?.data?.message;
      notifyError(message, "Could not load centre detail");
      return rejectWithValue(message || "Could not load centre detail");
    }
  }
);

export const fetchChangeLog = createAsyncThunk(
  "ccOnboarding/fetchChangeLog",
  async ({ centerCode, ...params }, { rejectWithValue }) => {
    try {
      const { data } = await ccOnboardingApi.getChangeLog(centerCode, params);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Could not load the change log");
    }
  }
);

// ---------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------

const { draftKey: persistedDraftKey, onboardingType: persistedOnboardingType } =
  readPersistedDraftKey();

const initialState = {
  draftKey: persistedDraftKey,
  onboardingType: persistedOnboardingType,

  // true the moment any field changes after the last successful save —
  // this is what useNavigationGuard reads to decide whether to block.
  isDirty: false,

  stepStatus: {
    1: { complete: false, saving: false, error: null },
    2: { complete: false, saving: false, error: null },
    3: { complete: false, saving: false, error: null },
    4: { complete: false, saving: false, error: null },
    5: { complete: false, saving: false, error: null },
    6: { complete: false, saving: false, error: null },
    7: { complete: false, saving: false, error: null },
  },

  workflowChain: { status: "idle", data: [], error: null },

  submit: {
    status: "idle", // idle | submitting | succeeded | failed
    error: null,
    centerCode: null,
    onboardingStatus: null,
    currentApproverLevel: null,
  },

  workflowAction: { status: "idle", error: null, lastAction: null },

  list: { status: "idle", data: null, error: null },
  workQueue: { status: "idle", data: null, error: null },
  detail: { status: "idle", data: null, error: null },
  changeLog: { status: "idle", data: null, error: null },
};

const ccOnboardingSlice = createSlice({
  name: "ccOnboarding",
  initialState,
  reducers: {
    /** Dispatched alongside every local form-field edit (see the small
     *  dispatch wrapper in the wizard component) so redux — and by
     *  extension the navigation guard — knows there is unsaved work. */
    markDirty(state) {
      state.isDirty = true;
    },
    clearDirty(state) {
      state.isDirty = false;
    },
    setOnboardingType(state, action) {
      state.onboardingType = action.payload;
    },
    resetWizard(state) {
      clearPersistedDraftKey();
      Object.assign(state, initialState, {
        draftKey: null,
        onboardingType: "CC",
        stepStatus: initialState.stepStatus,
      });
    },
    /**
     * Points the wizard at the right draft before an edit starts.
     *
     * Two cases, and getting them the wrong way round is the main hazard in
     * this flow:
     *
     *  - The facility already has a parked edit (`pendingEdit.draftKey`). Reuse
     *    it. Starting a fresh draft instead gets the PUT refused with "already
     *    has an edit awaiting approval" — after the user has re-filled seven
     *    screens.
     *  - It has none. Clear the draftKey so a new one is minted on the first
     *    step save. Without this the persisted key from a PREVIOUS, unrelated
     *    draft is reused (it survives in localStorage), so the edit's steps are
     *    appended to that stale draft and then parked against this facility.
     *
     * Step completion is reset either way: the ticks belong to whichever draft
     * we are now pointing at, not to the last one this browser touched.
     */
    beginEdit(state, action) {
      const parkedDraftKey = action.payload?.draftKey || null;
      state.draftKey = parkedDraftKey;
      state.isDirty = false;
      state.stepStatus = initialState.stepStatus;
      state.submit = { ...initialState.submit };
      if (parkedDraftKey) {
        persistDraftKey(parkedDraftKey, state.onboardingType);
      } else {
        clearPersistedDraftKey();
      }
    },
    hydrateStepCompletion(state, action) {
      // action.payload: {1:true, 2:true, 3:false, ...} from GET /progress
      Object.entries(action.payload || {}).forEach(([stepNo, complete]) => {
        if (state.stepStatus[stepNo]) state.stepStatus[stepNo].complete = !!complete;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // -- workflow chain -------------------------------------------------
      .addCase(fetchWorkflowChain.pending, (state) => {
        state.workflowChain.status = "loading";
        state.workflowChain.error = null;
      })
      .addCase(fetchWorkflowChain.fulfilled, (state, action) => {
        state.workflowChain.status = "succeeded";
        state.workflowChain.data = action.payload;
      })
      .addCase(fetchWorkflowChain.rejected, (state, action) => {
        state.workflowChain.status = "failed";
        state.workflowChain.error = action.payload;
      })

      // -- save step --------------------------------------------------------
      .addCase(saveWizardStep.pending, (state, action) => {
        const { stepNo } = action.meta.arg;
        state.stepStatus[stepNo].saving = true;
        state.stepStatus[stepNo].error = null;
      })
      .addCase(saveWizardStep.fulfilled, (state, action) => {
        const { stepNo, data } = action.payload;
        state.stepStatus[stepNo].saving = false;
        state.stepStatus[stepNo].complete = !!data?.isComplete || true;
        state.stepStatus[stepNo].savedAt = new Date().toISOString();
        if (data?.draftKey && data.draftKey !== state.draftKey) {
          state.draftKey = data.draftKey;
          persistDraftKey(data.draftKey, state.onboardingType);
        }
        // The step that was just saved is no longer "unsaved work" — but
        // other steps the user has since started editing might still be,
        // so only clear the global flag when nothing else is mid-edit.
        state.isDirty = false;
      })
      .addCase(saveWizardStep.rejected, (state, action) => {
        const stepNo = action.payload?.stepNo ?? action.meta.arg.stepNo;
        state.stepStatus[stepNo].saving = false;
        state.stepStatus[stepNo].error = action.payload?.message || "Save failed";
      })

      // -- resume draft -----------------------------------------------------
      .addCase(fetchDraft.fulfilled, (state, action) => {
        state.draftKey = action.payload?.draftKey || state.draftKey;
      })
      .addCase(fetchDraftProgress.fulfilled, (state, action) => {
        Object.entries(action.payload || {}).forEach(([stepNo, complete]) => {
          if (state.stepStatus[stepNo]) state.stepStatus[stepNo].complete = !!complete;
        });
      })

      // -- submit -------------------------------------------------------------
      .addCase(submitOnboarding.pending, (state) => {
        state.submit.status = "submitting";
        state.submit.error = null;
      })
      .addCase(submitOnboarding.fulfilled, (state, action) => {
        state.submit.status = "succeeded";
        state.submit.centerCode = action.payload?.code || action.payload?.centerCode || null;
        state.submit.onboardingStatus = action.payload?.onboardingStatus ?? null;
        state.submit.currentApproverLevel = action.payload?.currentApproverLevel ?? null;
        state.isDirty = false;
        // Submit closes the draft server-side; nothing left to resume.
        clearPersistedDraftKey();
      })
      .addCase(submitOnboarding.rejected, (state, action) => {
        state.submit.status = "failed";
        state.submit.error = action.payload || "Submit failed";
      })

      // -- update (edit an already-onboarded facility) -------------------------
      .addCase(updateOnboarding.pending, (state) => {
        state.submit.status = "submitting";
        state.submit.error = null;
      })
      .addCase(updateOnboarding.fulfilled, (state, action) => {
        state.submit.status = "succeeded";
        state.submit.centerCode = action.payload?.code || action.meta.arg.centerCode || null;
        state.submit.onboardingStatus = action.payload?.onboardingStatus ?? null;
        state.submit.currentApproverLevel = action.payload?.currentApproverLevel ?? null;
        state.isDirty = false;
        clearPersistedDraftKey();
      })
      .addCase(updateOnboarding.rejected, (state, action) => {
        state.submit.status = "failed";
        state.submit.error = action.payload || "Update failed";
      })

      // -- approve / reject / resubmit -----------------------------------------
      .addCase(approveCentre.pending, (state) => {
        state.workflowAction = { status: "loading", error: null, lastAction: "approve" };
      })
      .addCase(approveCentre.fulfilled, (state, action) => {
        state.workflowAction = { status: "succeeded", error: null, lastAction: "approve" };
        state.submit.onboardingStatus = action.payload?.onboardingStatus ?? state.submit.onboardingStatus;
        state.submit.currentApproverLevel =
          action.payload?.currentApproverLevel ?? state.submit.currentApproverLevel;
      })
      .addCase(approveCentre.rejected, (state, action) => {
        state.workflowAction = { status: "failed", error: action.payload, lastAction: "approve" };
      })
      .addCase(rejectCentre.pending, (state) => {
        state.workflowAction = { status: "loading", error: null, lastAction: "reject" };
      })
      .addCase(rejectCentre.fulfilled, (state, action) => {
        state.workflowAction = { status: "succeeded", error: null, lastAction: "reject" };
        state.submit.onboardingStatus = action.payload?.onboardingStatus ?? state.submit.onboardingStatus;
      })
      .addCase(rejectCentre.rejected, (state, action) => {
        state.workflowAction = { status: "failed", error: action.payload, lastAction: "reject" };
      })
      .addCase(resubmitCentre.pending, (state) => {
        state.workflowAction = { status: "loading", error: null, lastAction: "resubmit" };
      })
      .addCase(resubmitCentre.fulfilled, (state, action) => {
        state.workflowAction = { status: "succeeded", error: null, lastAction: "resubmit" };
        state.submit.onboardingStatus = action.payload?.onboardingStatus ?? state.submit.onboardingStatus;
        state.submit.currentApproverLevel =
          action.payload?.currentApproverLevel ?? state.submit.currentApproverLevel;
      })
      .addCase(resubmitCentre.rejected, (state, action) => {
        state.workflowAction = { status: "failed", error: action.payload, lastAction: "resubmit" };
      })

      // -- read screens -----------------------------------------------------
      .addCase(fetchList.pending, (state) => {
        state.list.status = "loading";
      })
      .addCase(fetchList.fulfilled, (state, action) => {
        state.list.status = "succeeded";
        state.list.data = action.payload;
      })
      .addCase(fetchList.rejected, (state, action) => {
        state.list.status = "failed";
        state.list.error = action.payload;
      })
      .addCase(fetchWorkQueue.pending, (state) => {
        state.workQueue.status = "loading";
      })
      .addCase(fetchWorkQueue.fulfilled, (state, action) => {
        state.workQueue.status = "succeeded";
        state.workQueue.data = action.payload;
      })
      .addCase(fetchWorkQueue.rejected, (state, action) => {
        state.workQueue.status = "failed";
        state.workQueue.error = action.payload;
      })
      .addCase(fetchDetail.pending, (state) => {
        state.detail.status = "loading";
      })
      .addCase(fetchDetail.fulfilled, (state, action) => {
        state.detail.status = "succeeded";
        state.detail.data = action.payload;
      })
      .addCase(fetchDetail.rejected, (state, action) => {
        state.detail.status = "failed";
        state.detail.error = action.payload;
      })
      .addCase(fetchChangeLog.pending, (state) => {
        state.changeLog.status = "loading";
      })
      .addCase(fetchChangeLog.fulfilled, (state, action) => {
        state.changeLog.status = "succeeded";
        state.changeLog.data = action.payload;
      })
      .addCase(fetchChangeLog.rejected, (state, action) => {
        state.changeLog.status = "failed";
        state.changeLog.error = action.payload;
      });
  },
});

export const { markDirty, clearDirty, setOnboardingType, resetWizard, beginEdit, hydrateStepCompletion } =
  ccOnboardingSlice.actions;

// ---------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------
export const selectCcOnboarding = (state) => state.ccOnboarding;
export const selectIsDirty = (state) => state.ccOnboarding.isDirty;
export const selectDraftKey = (state) => state.ccOnboarding.draftKey;
export const selectStepStatus = (state) => state.ccOnboarding.stepStatus;
export const selectSubmitState = (state) => state.ccOnboarding.submit;
export const selectOnboardingWorkflow = (state) => ({
  list: state.ccOnboarding.list,
  workQueue: state.ccOnboarding.workQueue,
  workflowAction: state.ccOnboarding.workflowAction,
});

/**
 * The unapplied edit on the facility currently loaded in `detail`, or null.
 *
 * Present only for an onboarded facility whose edit has not been approved yet.
 * `changes[]` is the from/to diff an approver decides on; the detail tabs
 * alongside it still hold the LIVE values, which is the point of the whole
 * hold — so never merge the two.
 */
export const selectPendingEdit = (state) => state.ccOnboarding.detail.data?.pendingEdit || null;

export default ccOnboardingSlice.reducer;